import Joi from "joi";

export const EMAIL_PATTERN =
  /^[^\s@`!@#$%^&*()=]+@[^\s@`!@#$%^&*()=]+\.[^\s@`!@#$%^&*()=]+$/;

export const EMAIL_PATTERN_SIMPLE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email: string): boolean => {
  return EMAIL_PATTERN.test(email);
};

export const isValidEmailSimple = (email: string): boolean => {
  return EMAIL_PATTERN_SIMPLE.test(email);
};

/** A required email whose refusals are the translation keys given, translated where they are shown. */
export const emailField = (required: string, invalid: string) =>
  Joi.string().trim().required().regex(EMAIL_PATTERN).messages({
    "any.required": required,
    "string.empty": required,
    "string.pattern.base": invalid,
  });

/** A form message given as a `validation.*` key is translated; any other one, Joi's own text, is shown as it is. */
export const shownFieldError = (
  t: (key: string) => unknown,
  message?: string
): string | undefined =>
  message?.startsWith("validation.") ? String(t(message)) : message;

/** Changing one's email: the current one, the new one and its confirmation. Messages are `validation.*` keys. */
export const emailChangeSchema = Joi.object({
  user_email: Joi.string().trim().required().messages({
    "any.required": "validation.emailRequired",
    "string.empty": "validation.emailRequired",
  }),
  new_user_email: emailField(
    "validation.emailRequired",
    "validation.emailInvalid"
  ),
  new_user_email_confirmation: Joi.string()
    .trim()
    .required()
    .custom((value, helpers) =>
      value === helpers.state.ancestors[0].new_user_email?.trim()
        ? value
        : helpers.error("any.only")
    )
    .messages({
      "any.only": "validation.emailsMismatch",
      "any.required": "validation.emailConfirmRequired",
      "string.empty": "validation.emailConfirmRequired",
    }),
});

const passwordField = () =>
  Joi.string().trim().required().messages({
    "any.required": "validation.passwordRequired",
    "string.empty": "validation.passwordRequired",
  });

const passwordConfirmation = () =>
  Joi.any().valid(Joi.ref("new_password")).required().messages({
    "any.only": "validation.passwordsMismatch",
    "any.required": "validation.confirmPasswordRequired",
  });

const EMAIL_REQUIRED = {
  "any.required": "validation.emailRequired",
  "string.empty": "validation.emailRequired",
};

// Not emailField: customer registration forms accept a&b@x.com, which EMAIL_PATTERN would lock out of login.
export const loginSchema = Joi.object({
  user_email: Joi.string()
    .trim()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      ...EMAIL_REQUIRED,
      "string.email": "validation.emailInvalid",
    }),
  user_password: passwordField(),
}).required();

export const forgotPasswordSchema = Joi.object({
  user_email: Joi.string().trim().required().messages(EMAIL_REQUIRED),
}).required();

export const resetPasswordSchema = Joi.object({
  new_password: passwordField(),
  new_confirm_password: passwordConfirmation(),
}).required();

export const changePasswordSchema = Joi.object({
  actual_password: passwordField(),
  new_password: passwordField(),
  new_confirm_password: passwordConfirmation(),
}).required();
